import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';

import { iconConstants, routesConstants } from '@/constants';
import Icons from '@/icons';
import { authHelpers, generalHelpers } from '@/helpers';
import { authTypes } from '@/types';
import { Button, Input } from '@/kaya';
import { songActions, userActions } from '@/actions';
import logo from '@/assets/upperLogin-image.png';
import { songsStore, useArtistStore, withdrawStore } from '@/store';

import styles from './BankInfo.module.scss';
import { ICONS } from '@/constants/icons.constants';

const BankInfo = () => {
  const navigate = useNavigate();

  const { setArtist, setAuthentication, artist } = useArtistStore();
  const { setSongs } = songsStore();
  const { setWithdraw } = withdrawStore();

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<authTypes.BankInfo>(
    authTypes.INITIAL_BANK_INFO_VALUES,
  );

  const [err, setErr] = React.useState({
    bank_name: '',
    acc_no: '',
    acc_holder_name: '',
    cin_no: '',
    iban: '',
    swift_code: '',
    branch_code: '',
  });

  const handleChange = (id: string | object) => (value: string | object | boolean | File) => {
    if (typeof id !== 'object') {
      setFormValues({
        ...formValues,
        [id]: value,
      });
      setErr({
        ...err,
        [id]: '',
      });
    } else {
      setFormValues({
        ...formValues,
        ...id,
      });
    }
  };

  const handleSignUp = async () => {
    const error = {
      bank_name: authHelpers.validateBankName(get(formValues, 'bank_name', '')),
      acc_no: authHelpers.validateAccountNo(get(formValues, 'acc_no', '').toString()),
      acc_holder_name: authHelpers.validateAccountHolderName(
        get(formValues, 'acc_holder_name', ''),
      ),
      cin_no: authHelpers.validateCinNumber(get(formValues, 'cin_no', '').toString()),
      iban: authHelpers.validateIbanNumber(get(formValues, 'iban', '')),
      swift_code: authHelpers.validateSwiftCode(get(formValues, 'swift_code', '')),
      branch_code: authHelpers.validateBranchName(get(formValues, 'branch_code', '')),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr({
        ...err,
        ...error,
      });
      return;
    }
    setLoading(true);
    // write api call here
    const data = await userActions.fetchArtistAction(artist.id || '');
    if (data.success) {
      setArtist(get(data, 'data.data', {}));
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
      });
    }

    const songsData = await songActions.fetchSongsAction(get(artist, 'id', ''));

    if (songsData.success) {
      setSongs(get(songsData, 'data', []));
    }

    const WithdrawalData = await userActions.fetchWithdrawalAction(get(data, 'data.data.id', {}));
    if (WithdrawalData.success) {
      setWithdraw(get(WithdrawalData, 'data', {}));
    }

    const updateArtist = await userActions.updateArtistAction({
      payload: {
        bank_name: get(formValues, 'bank_name', ''),
        acc_no: Number(get(formValues, 'acc_no', '')),
        acc_holder_name: get(formValues, 'acc_holder_name', ''),
        cin_no: Number(get(formValues, 'cin_no', '')),
        iban: get(formValues, 'iban', ''),
        swift_code: get(formValues, 'swift_code', ''),
        branch_code: get(formValues, 'branch_code', ''),
      },
      id: artist.id,
    });

    if (updateArtist.success) {
      setArtist(get(updateArtist, 'data', {}));
      navigate(routesConstants.ROUTES.HOME);
    }
    setLoading(false);
  };

  const handleSkip = async () => {
    const data = await userActions.fetchArtistAction(artist.id);

    if (data.success) {
      setArtist(get(data, 'data.data', {}));
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
      });
    }

    const songsData = await songActions.fetchSongsAction(get(data, 'data.data.id', {}));

    if (songsData.success) {
      setSongs(get(songsData, 'data', []));
    }

    navigate(routesConstants.ROUTES.HOME);
  };

  return (
    <div className={styles.BankInfo}>
      <div className={styles.header}>
        <div></div>
        <div className={styles.logo}>
          <img src={logo} alt="logo" className={styles.logoImg} />
        </div>
        <div className={styles.skipButton} onClick={handleSkip}>
          <p className={styles.text}>Skip</p>
          <Icons type={ICONS.SKIP_ARROW} />
        </div>
      </div>

      <div className={styles.box}>
        <h1 className={styles.title}>Bank Information</h1>
        <Input
          placeholder="Bank Name"
          containerClassName={styles.input1}
          value={formValues.bank_name}
          onChange={handleChange('bank_name')}
          errors={err}
          id="bank_name"
        />
        <Input
          type="number"
          placeholder="A/c Number"
          containerClassName={styles.input1}
          value={formValues.acc_no}
          onChange={handleChange('acc_no')}
          errors={err}
          id="acc_no"
          maxLength={10}
        />
        <Input
          placeholder="A/c Holder Name"
          containerClassName={styles.input1}
          value={formValues.acc_holder_name}
          onChange={handleChange('acc_holder_name')}
          errors={err}
          id="acc_holder_name"
        />
        <Input
          placeholder="CIN Number"
          containerClassName={styles.input1}
          value={formValues.cin_no}
          onChange={handleChange('cin_no')}
          errors={err}
          id="cin_no"
        />
        <Input
          placeholder="IBAN"
          containerClassName={styles.input1}
          value={formValues.iban}
          onChange={handleChange('iban')}
          errors={err}
          id="iban"
        />
        <Input
          placeholder="Swift Code"
          containerClassName={styles.input1}
          value={formValues.swift_code}
          onChange={handleChange('swift_code')}
          errors={err}
          id="swift_code"
        />
        <Input
          placeholder="Branch"
          containerClassName={styles.input2}
          value={formValues.branch_code}
          onChange={handleChange('branch_code')}
          errors={err}
          id="branch_code"
          onEnterClick={handleSignUp}
        />
        <Button className={styles.btn} onClick={handleSignUp} isLoading={loading} type="Primary">
          Sign Up
        </Button>
      </div>
    </div>
  );
};

export default BankInfo;
